import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckboxGroupSettingsSection } from './styles';
import { CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR } from '../../../../artboard/CheckboxSettings/transactionValues';
import { useTranslation } from 'react-i18next';
import {
  RowSection,
  FieldTitle,
  FieldContent,
} from '../../../../components/FillableSettings/styles';
import { Input, InputNumber, Select, Switch } from 'antd';
import { CustomIcon } from '../../../../components/GroupSettings/styles';
import { CircleBoxIcon, SquareBoxIcon } from '../../../../artboard/CheckboxSettings/CheckBoxIcons';
import TextBlockWithQuillRenderer from '../../TextBlockWithQuillRenderer';
import {
  constructAutoSaveJSON,
  constructQuillJSON,
  convertContractToReact,
} from '../../../../utils/TextBlockHelper';
import { dummyCallback } from '../../../../utils/complexDocumentConstants';
import { useDefaultStyleContext } from '../../../../context/complexDefaultStyleContext';
import { CONTEXT_KEYS, useArtboardContext } from '../../../../context/complexArtboardContext';

const Option = Select.Option;

const NAMESPACES = ['CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR'];

export const CheckboxGroupSettings = ({
  element,
  updateElementDetails,
  onGroupNameChange,
  isRadio = false,
  updateTextStyle,
  updateElementStyles,
  selected,
  updateElementRect,
}) => {
  const { t } = useTranslation(NAMESPACES);
  const { contents, style } = element;

  const localGroupName = contents?.formOptions?.groupName || '';

  const [groupName, setGroupName] = useState(localGroupName);
  const [randomKey, setRandomKey] = useState(1);
  const [canUpdateRandomKey, setCanUpdateRandomKey] = useState(true);
  const inputRef = useRef(false);

  const quillContents = useMemo(() => {
    const { fontFamily, fontSize, fontWeight, color, backgroundColor } = style;
    const contents = [
      {
        contentType: 'PARAGRAPH',
        content: 'test',
        style: { fontFamily, fontSize, fontWeight, color, backgroundColor },
      },
      { contentType: 'LINE_BREAK', content: '\n' },
    ];
    return contents;
  }, [style]);

  const getDefaultStyleSelector = useCallback(
    store => ({ fontFamily: store.fontFamily, fontSize: store.fontSize }),
    [],
  );
  const [documentStyle] = useDefaultStyleContext(getDefaultStyleSelector);

  const textElementSelector = useCallback(store => store[CONTEXT_KEYS.TEXT_ELEMENT_REF], []);
  const [invisibleTextRef] = useArtboardContext(
    textElementSelector,
    `checkboxGroup-${element.uuid}`,
  );

  if (
    invisibleTextRef?.current?.editor &&
    !invisibleTextRef?.current?.editor?.hasFocus() &&
    !inputRef.current
  ) {
    invisibleTextRef.current.editor.setSelection(0, invisibleTextRef.current.editor.getLength());
    invisibleTextRef.current.editor.focus();
  }

  useEffect(() => {
    if (canUpdateRandomKey) {
      setRandomKey(oldKey => oldKey + 1);
    }
  }, [quillContents]);

  useEffect(() => {
    setGroupName(localGroupName);
  }, [localGroupName]);

  const onRadioGroupNameChange = useCallback(
    e => {
      if (e.target.value.split('.').length <= 1) {
        setGroupName(e.target.value);
        onGroupNameChange(e.target.value);
      }
      inputRef.current = true;
    },
    [onGroupNameChange],
  );

  const saveTextStyle = useCallback(
    contents => {
      const resultContent = constructAutoSaveJSON(
        contents,
        documentStyle?.fontFamily,
        documentStyle?.fontSize,
        [],
      );
      const styleContent = resultContent[0].style;
      const textStyle = {
        fontFamily: styleContent.fontFamily,
        fontSize: styleContent.fontSize,
        color: styleContent.color,
        backgroundColor: styleContent.backgroundColor,
        fontWeight: styleContent?.fontWeight?.length > 0 ? styleContent?.fontWeight : [],
      };
      updateTextStyle(textStyle);
      requestAnimationFrame(() => updateElementRect());
    },
    [documentStyle, updateTextStyle, updateElementRect],
  );

  return (
    <CheckboxGroupSettingsSection
      onMouseEnter={() => setCanUpdateRandomKey(false)}
      onMouseLeave={() => setCanUpdateRandomKey(true)}
    >
      {!isRadio && (
        <FieldTitle isTitle={1}>
          {t(
            CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.CHECK_BOX_GROUP_SETTINGS_TITLE.key,
            CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.CHECK_BOX_GROUP_SETTINGS_TITLE.value,
          )}
        </FieldTitle>
      )}
      {isRadio && (
        <FieldTitle isTitle={1}>
          {t(
            CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.RADIO_BUTTON_GROUP_SETTINGS_TITLE.key,
            CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.RADIO_BUTTON_GROUP_SETTINGS_TITLE.value,
          )}
        </FieldTitle>
      )}
      {
        <RowSection>
          <FieldTitle>
            {t(
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.GROUP_NAME_LABEL.key,
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.GROUP_NAME_LABEL.value,
            )}
          </FieldTitle>
          <FieldContent>
            <Input
              value={groupName}
              placeholder={t(
                CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.GROUP_NAME_PLACEHOLDER.key,
                CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.GROUP_NAME_PLACEHOLDER.value,
              )}
              onChange={onRadioGroupNameChange}
              onFocus={() => {
                inputRef.current = true;
              }}
              onBlur={() => {
                inputRef.current = false;
                setRandomKey(oldKey => oldKey + 1);
              }}
            />
          </FieldContent>
        </RowSection>
      }
      {!isRadio && (
        <RowSection>
          <FieldTitle>
            {t(
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.CHECK_STYLE_LABEL.key,
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.CHECK_STYLE_LABEL.value,
            )}
          </FieldTitle>
          <FieldContent>
            <Select
              value={contents?.formOptions?.checkStyle}
              style={{ width: 100 }}
              onChange={e => updateElementDetails(e, 'checkStyle')}
            >
              <Option value={'4'}>
                <CustomIcon type={'check-square'} />
              </Option>
              <Option value={'8'}>
                <CustomIcon type={'close-square'} />
              </Option>
              <Option value={'n'}>
                <CustomIcon component={SquareBoxIcon} />
              </Option>
              <Option value={'l'}>
                <CustomIcon component={CircleBoxIcon} />
              </Option>
            </Select>
          </FieldContent>
        </RowSection>
      )}
      <RowSection>
        <FieldTitle>
          {t(
            CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.READ_ONLY_LABEL.key,
            CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.READ_ONLY_LABEL.value,
          )}
        </FieldTitle>
        <FieldContent>
          <Switch
            checkedChildren={t(
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.READ_ONLY_YES_SWITCH.key,
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.READ_ONLY_YES_SWITCH.value,
            )}
            unCheckedChildren={t(
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.READ_ONLY_NO_SWITCH.key,
              CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.READ_ONLY_NO_SWITCH.value,
            )}
            checked={contents?.formOptions?.isReadOnly}
            onChange={e => updateElementDetails(e, 'isReadOnly')}
          />
        </FieldContent>
      </RowSection>
      <RowSection>
        <FieldTitle>
          {t(
            CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.DIRECTION_LABEL.key,
            CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.DIRECTION_LABEL.value,
          )}
        </FieldTitle>
        <FieldContent>
          <Select
            value={contents?.formOptions?.direction}
            style={{ width: 100 }}
            onChange={e => {
              updateElementDetails(e, 'direction');
              requestAnimationFrame(() => updateElementRect());
            }}
          >
            <Option value={'horizontal'}>horizontal</Option>
            <Option value={'vertical'}>vertical</Option>
          </Select>
        </FieldContent>
      </RowSection>
      <RowSection>
        <FieldTitle>
          {t(
            CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.WIDTH_HEIGHT_LABEL.key,
            CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.WIDTH_HEIGHT_LABEL.value,
          )}
        </FieldTitle>
        <FieldContent>
          <InputNumber
            value={contents.style.width}
            onChange={value => {
              updateElementStyles({ height: value, width: value });
              requestAnimationFrame(() => updateElementRect());
            }}
          />
          <span style={{ marginLeft: '5px' }}>px</span>
        </FieldContent>
      </RowSection>
      <FieldTitle isTitle={1}>
        {t(
          CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.TEXT_SETTINGS_LABEL.key,
          CHECKBOX_RADIO_BUTTON_SETTINGS_SIDEBAR.TEXT_SETTINGS_LABEL.value,
        )}
      </FieldTitle>
      <RowSection>
        <div className="invisible-quill">
          <div id={`checkbox-group-${element.uuid}`} style={{ minHeight: 88 }} />
          <TextBlockWithQuillRenderer
            key={randomKey}
            defaultContent={constructQuillJSON(
              quillContents,
              documentStyle?.fontFamily,
              documentStyle?.fontSize,
              [],
              false,
              {},
            )}
            saveBlock={saveTextStyle}
            isFromCreativeBlock={false}
            itemUUID={`cell-${element.uuid}`}
            selected={selected}
            noFocus={true}
            enableSideBar={() => {}}
            scaling={1}
            dataSet={{}}
            isPreview={false}
            isFontSidebarAbsolute={true}
            invisible={true}
            adjustScroll={dummyCallback}
            toolbarDOM={`checkbox-group-${element.uuid}`}
            textEditable={true}
            setTextEditable={dummyCallback}
          >
            {convertContractToReact(
              quillContents,
              documentStyle?.fontFamily,
              documentStyle?.fontSize,
              [],
            )}
          </TextBlockWithQuillRenderer>
        </div>
      </RowSection>
    </CheckboxGroupSettingsSection>
  );
};
